import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlatformClinic,
  PlatformClinicsAPI,
  ClinicDocItem,
  ClinicUserGroup,
  ClinicDocumentsResponse,
} from "../../services/platformClinics.service";
import KycStatusBadge from "./KycStatusBadge";

interface Props {
  isOpen: boolean;
  clinic: PlatformClinic | null;
  onClose: () => void;
}

/** Left-edge status glyph mirroring the mockup (check / cross / dot / dash). */
const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  const base = "w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0";
  if (status === "verified") return <span className={`${base} bg-green-100 text-green-600`}>✓</span>;
  if (status === "rejected") return <span className={`${base} bg-red-100 text-red-500`}>✕</span>;
  if (status === "submitted") return <span className={`${base} bg-amber-100 text-amber-500`}>●</span>;
  return <span className={`${base} bg-slate-100 text-slate-300`}>–</span>;
};

const DocRow: React.FC<{ doc: ClinicDocItem; onOpen: (doc: ClinicDocItem) => void }> = ({ doc, onOpen }) => {
  const clickable = doc.status !== "not_uploaded" && !!doc.doc_id;
  // Doctor docs name the specific doctor; clinic docs show the raw doc_type as in the mockup.
  const subline = doc.scope === "doctor"
    ? `Doctor · ${doc.doctor_name ?? doc.doc_type}`
    : `Clinic · ${doc.doc_type}`;

  const body = (
    <>
      <StatusIcon status={doc.status} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-800">{doc.label}</span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
            doc.required ? "bg-red-50 text-red-500" : "bg-slate-100 text-slate-400"
          }`}>
            {doc.required ? "REQUIRED" : "OPTIONAL"}
          </span>
        </div>
        <p className="text-xs text-slate-400 mt-0.5">{subline}</p>
        {doc.rejection_reason && (
          <p className="text-xs text-red-500 mt-1">⚠ {doc.rejection_reason}</p>
        )}
      </div>
      <KycStatusBadge status={doc.status} />
      {clickable && <span className="text-slate-300 ml-1">›</span>}
    </>
  );

  if (clickable) {
    return (
      <button
        onClick={() => onOpen(doc)}
        className="w-full flex items-center gap-3 px-2 py-3 rounded-lg text-left hover:bg-slate-50 transition-colors"
      >
        {body}
      </button>
    );
  }
  return <div className="w-full flex items-center gap-3 px-2 py-3">{body}</div>;
};

/** Per-user summary pill shown on the collapsed accordion header. */
const UserStatusPill: React.FC<{ status: string; verified: number; total: number }> = ({ status, verified, total }) => {
  const map: Record<string, [string, string]> = {
    verified: ["bg-green-50 border-green-200 text-green-700", "All verified"],
    rejected: ["bg-red-50 border-red-200 text-red-700", "Action needed"],
    pending: ["bg-yellow-50 border-yellow-200 text-yellow-800", "Under review"],
    incomplete: ["bg-slate-50 border-slate-200 text-slate-400", `${verified}/${total} verified`],
  };
  const [cls, label] = map[status] ?? map.incomplete;
  return <span className={`inline-flex items-center rounded-full text-xs font-semibold px-2.5 py-0.5 border ${cls}`}>{label}</span>;
};

const UserGroup: React.FC<{ user: ClinicUserGroup; onOpen: (doc: ClinicDocItem) => void }> = ({ user, onOpen }) => {
  const [open, setOpen] = useState(false);
  const initials = user.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-3 py-3 hover:bg-slate-50 transition-colors text-left"
      >
        <span className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold flex items-center justify-center flex-shrink-0">
          {initials}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{user.name}</p>
          <p className="text-xs text-slate-400">{user.role}</p>
        </div>
        <UserStatusPill status={user.overall_status} verified={user.required_verified} total={user.required_total} />
        <span className={`text-slate-300 transition-transform ${open ? "rotate-90" : ""}`}>›</span>
      </button>

      {open && (
        <div className="px-3 pb-2 border-t border-slate-50 divide-y divide-slate-50">
          {user.documents.map(d => <DocRow key={d.doc_type} doc={d} onOpen={onOpen} />)}
        </div>
      )}
    </div>
  );
};

const ClinicDocumentsModal: React.FC<Props> = ({ isOpen, clinic, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<ClinicDocumentsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !clinic) return;
    setLoading(true);
    setError(null);
    setData(null);
    PlatformClinicsAPI.getClinicDocuments(clinic.clinic_id)
      .then(setData)
      .catch(() => setError("Failed to load documents. Please try again."))
      .finally(() => setLoading(false));
  }, [isOpen, clinic]);

  if (!isOpen || !clinic) return null;

  // Deep-link into the existing review screen, which renders purely from router state.
  const openReview = (doc: ClinicDocItem) => {
    if (!data || !doc.doc_id) return;
    const item = {
      kyc_doc_id: doc.doc_id,
      doctor_id: doc.doctor_id,
      doctor_name: doc.doctor_name ?? "—",
      doc_type: doc.doc_type,
      status: doc.status,
      submitted_at: doc.submitted_at ?? "",
      clinic_id: data.clinic_id,
      clinic_name: data.clinic_name,
      view_url: doc.view_url,
      subject_kind: doc.subject_kind,
      clinic_doc_id: doc.subject_kind === "clinic" ? doc.doc_id : undefined,
      rejection_reason: doc.rejection_reason,
      verified_at: doc.verified_at,
      verified_by: null,
      other_docs: [],
    };
    navigate(`/platform/kyc/${doc.doc_id}`, { state: { doctor_id: doc.doctor_id ?? "", item } });
  };

  const clinicMandatory = data?.clinic_documents.filter(d => d.required) ?? [];
  const clinicOptional = data?.clinic_documents.filter(d => !d.required) ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-blue-600">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-slate-800 leading-tight">Clinic Documents</h2>
                {data?.kyc_complete && (
                  <span className="inline-flex items-center rounded-full bg-green-50 border border-green-200 text-green-700 text-[10px] font-semibold px-2 py-0.5">
                    ✓ KYC COMPLETE
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {clinic.name}{clinic.location ? ` · ${clinic.location}` : ""}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none font-bold"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-2 overflow-y-auto flex-1">
          {loading && <p className="text-sm text-slate-400 py-8 text-center">Loading documents…</p>}
          {error && <p className="text-sm text-red-500 py-8 text-center">{error}</p>}

          {data && (
            <>
              {data.kyc_complete && (
                <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 mb-4">
                  <p className="text-sm font-medium text-green-700">All mandatory documents verified</p>
                  <p className="text-xs text-green-600 mt-0.5">
                    Clinic meets onboarding requirements. Optional docs can still be added.
                  </p>
                </div>
              )}

              {/* ── Clinic (institutional) documents ── */}
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-semibold tracking-wide text-slate-400">CLINIC DOCUMENTS</p>
                <p className="text-[11px] text-slate-300">
                  mandatory {data.mandatory_verified}/{data.mandatory_total} · optional {data.optional_verified}/{data.optional_total}
                </p>
              </div>
              <div className="divide-y divide-slate-50 mb-5">
                {[...clinicMandatory, ...clinicOptional].map(d => (
                  <DocRow key={d.doc_type} doc={d} onOpen={openReview} />
                ))}
              </div>

              {/* ── Per-user (doctor / admin) documents ── */}
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold tracking-wide text-slate-400">STAFF DOCUMENTS</p>
                <p className="text-[11px] text-slate-300">tap a person to view their KYC</p>
              </div>
              {data.users.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No doctors or admins with KYC records yet.</p>
              ) : (
                <div className="space-y-2 pb-2">
                  {data.users.map(u => <UserGroup key={u.doctor_id} user={u} onOpen={openReview} />)}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100">
          <p className="text-xs text-slate-400">
            Read-only. Click an uploaded document to verify or reject it in the review screen.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClinicDocumentsModal;
